import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent, type JSONContent } from '@tiptap/react';
import {
  Bold,
  Italic,
  Link as LinkIcon,
  Heading1,
  Heading2,
  Quote,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  ImagePlus,
  Plus,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { blockEditorExtensions } from './blockEditorExtensions';
import { compressImageToDataUrl } from './imageUpload';

function ToolbarButton({
  active,
  onClick,
  disabled,
  title,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={cn('h-8 w-8', active && 'bg-accent text-accent-foreground')}
    >
      {children}
    </Button>
  );
}

interface BlockEditorProps {
  content: JSONContent;
  onChange: (json: JSONContent) => void;
}

/** Editor em blocos (título/subtítulo/texto/imagem/citação/lista + negrito/
 *  itálico/link/alinhamento), baseado no Tiptap — ver `blockEditorExtensions.ts`.
 *  Estado fica só na memória do editor; quem persiste é o chamador (`onChange`),
 *  nunca este componente sozinho (nenhuma gravação automática aqui). */
export function BlockEditor({ content, onChange }: BlockEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const [uploading, setUploading] = useState(false);
  // Posição (relativa ao wrapper do conteúdo) do botão "+" que aparece do lado
  // de uma linha vazia — deixa claro onde a imagem vai entrar ao clicar, em vez
  // de só confiar no botão da toolbar + "onde o cursor tiver ficado por último".
  const [plusButtonTop, setPlusButtonTop] = useState<number | null>(null);

  const editor = useEditor({
    extensions: blockEditorExtensions(),
    content,
    onUpdate: ({ editor: e }) => onChange(e.getJSON()),
    editorProps: {
      attributes: {
        // dark:prose-invert é obrigatório aqui: h1/h2/negrito/citação do plugin
        // de tipografia usam cor fixa própria (--tw-prose-headings etc.), não
        // herdam text-foreground — sem isso ficam escuros/ilegíveis no tema
        // escuro mesmo com o resto do texto certo.
        class:
          'prose prose-neutral dark:prose-invert max-w-none min-h-[320px] focus:outline-none font-body text-foreground [&_h1]:font-sans [&_h2]:font-sans',
      },
    },
  });

  useEffect(() => {
    if (!editor) return;

    const updatePlusButton = () => {
      const wrapper = contentWrapperRef.current;
      if (!wrapper || !editor.isFocused) {
        setPlusButtonTop(null);
        return;
      }
      const { $anchor, empty } = editor.state.selection;
      const onEmptyLine = empty && ['paragraph', 'heading'].includes($anchor.parent.type.name) && $anchor.parent.content.size === 0;
      if (!onEmptyLine) {
        setPlusButtonTop(null);
        return;
      }
      const coords = editor.view.coordsAtPos($anchor.pos);
      const wrapperRect = wrapper.getBoundingClientRect();
      // Nunca deixa o botão (que tem seu próprio offset negativo de -6 na
      // hora de renderizar, pra centralizar na linha) subir a ponto de
      // ficar embaixo da toolbar sticky — na 1ª linha do editor isso deixava
      // o botão colado/coberto por ela, impossível de clicar.
      setPlusButtonTop(Math.max(coords.top - wrapperRect.top, 6));
    };

    const handleBlur = () => setPlusButtonTop(null);

    editor.on('selectionUpdate', updatePlusButton);
    editor.on('update', updatePlusButton);
    editor.on('blur', handleBlur);
    editor.on('focus', updatePlusButton);

    return () => {
      // Handler nomeado em todo `off` — `.off(event)` sem handler remove TODOS
      // os listeners daquele evento no editor, o que apagaria os internos do
      // próprio Tiptap (ex. outros plugins ouvindo 'blur'), não só o nosso.
      editor.off('selectionUpdate', updatePlusButton);
      editor.off('update', updatePlusButton);
      editor.off('blur', handleBlur);
      editor.off('focus', updatePlusButton);
    };
  }, [editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('URL do link', previous ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().setLink({ href: url }).run();
  }, [editor]);

  const handlePickImage = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !editor) return;
    setUploading(true);
    try {
      const dataUrl = await compressImageToDataUrl(file);
      // Insere sempre na posição atual do cursor/seleção — é assim que "clicar
      // no texto onde quer a imagem" funciona: o usuário clica ali antes de
      // escolher o arquivo (pelo botão da toolbar fixa ou pelo "+" inline).
      editor.chain().focus().setImage({ src: dataUrl }).run();
    } catch (err) {
      console.error('Falha ao inserir imagem no artigo', err);
      window.alert(err instanceof Error ? err.message : 'Falha ao processar imagem');
    } finally {
      setUploading(false);
    }
  };

  if (!editor) return null;

  return (
    // Sem overflow-hidden aqui: ele cria um "scroll container" próprio mesmo
    // sem scrollbar (é assim que a spec de CSS define), e isso prende o filho
    // sticky dentro do card em vez de deixá-lo grudar no topo da ÁREA QUE
    // ROLA DE VERDADE (o painel do dashboard) — o sticky simplesmente não
    // fazia nada visível com overflow-hidden no pai. arredondamento do topo
    // fica só na própria barra (rounded-t-2xl), não depende mais de recorte.
    <div className="rounded-2xl border border-border bg-card">
      {/* sticky: fica visível ao rolar um artigo longo, só desce quando o
          card inteiro descer (pedido do usuário) — não soma com nenhum
          overflow-hidden entre aqui e a área que rola de verdade. */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-1 rounded-t-2xl border-b border-border bg-muted/95 backdrop-blur supports-[backdrop-filter]:bg-muted/80 p-2">
        <ToolbarButton title="Negrito" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Itálico" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Link" active={editor.isActive('link')} onClick={setLink}>
          <LinkIcon className="h-4 w-4" />
        </ToolbarButton>
        <div className="w-px h-6 bg-border mx-1" />
        <ToolbarButton
          title="Título"
          active={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        >
          <Heading1 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          title="Subtítulo"
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Citação" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <Quote className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton title="Lista" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          title="Lista numerada"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
        <div className="w-px h-6 bg-border mx-1" />
        <ToolbarButton
          title="Alinhar à esquerda"
          active={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
        >
          <AlignLeft className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          title="Centralizar"
          active={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
        >
          <AlignCenter className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          title="Alinhar à direita"
          active={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
        >
          <AlignRight className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          title="Justificar"
          active={editor.isActive({ textAlign: 'justify' })}
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
        >
          <AlignJustify className="h-4 w-4" />
        </ToolbarButton>
        <div className="w-px h-6 bg-border mx-1" />
        <ToolbarButton title="Inserir imagem" disabled={uploading} onClick={handlePickImage}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
        </ToolbarButton>
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFileChange} />
      </div>
      <div ref={contentWrapperRef} className="relative p-6">
        {plusButtonTop !== null && (
          // Centralizado horizontalmente no card (left-1/2 + translate), maior
          // que os ícones da toolbar — pedido do usuário. Só o topo acompanha
          // a linha vazia onde o cursor está.
          <button
            type="button"
            title="Inserir imagem aqui"
            disabled={uploading}
            onMouseDown={(e) => e.preventDefault()} // não rouba o foco do editor antes do clique
            onClick={handlePickImage}
            // z-20 > z-10 da toolbar sticky — sem isso, perto da 1ª linha o
            // botão ficava coberto pela toolbar e não recebia o clique.
            className="absolute left-1/2 z-20 -translate-x-1/2 flex h-10 w-10 items-center justify-center rounded-full border border-primary/30 bg-background text-primary shadow-sm hover:bg-primary/5 hover:border-primary transition-colors"
            style={{ top: plusButtonTop - 6 }}
          >
            <Plus className="h-5 w-5" />
          </button>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
